/**
 * MessageBubble — 单条消息气泡组件
 *
 * 职责：
 * - 根据角色（user/assistant）渲染不同样式的气泡
 * - 展示 AI 思考过程面板（可折叠）
 * - 展示消息加载动画（thinking 状态）
 * - 展示流式打字光标（streaming 状态）
 * - 展示错误状态（error 状态）
 *
 * 状态流转：
 *   thinking -> streaming -> completed
 *                            -> error
 */

import React from 'react';
import { ContentRenderer } from 'starry-sky-ui';
import { UserIcon, BotIcon, ChevronIcon } from '../Icons';
import type { ChatMessage } from '@/types';
import './index.scss';

interface MessageBubbleProps {
  /** 消息对象 */
  message: ChatMessage;
  /** 思考面板是否展开 */
  thinkingExpanded: boolean;
  /** 切换思考面板展开回调 */
  onToggleThinking: () => void;
}

/**
 * 格式化时间戳为相对时间
 * @param timestamp 时间戳（毫秒）
 * @returns 相对时间字符串
 */
const formatRelativeTime = (timestamp: number): string => {
  const now = Date.now();
  const diff = now - timestamp;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);

  if (minutes < 1) return '刚刚';
  if (minutes < 60) return `${minutes} 分钟前`;
  if (hours < 24) return `${hours} 小时前`;

  const date = new Date(timestamp);
  return `${date.getMonth() + 1}/${date.getDate()}`;
};

/**
 * 消息气泡组件
 */
const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  thinkingExpanded,
  onToggleThinking,
}) => {
  const isUser = message.role === 'user';
  const isThinking = message.status === 'thinking';
  const isStreaming = message.status === 'streaming';
  const hasThinking = !!message.thinking;

  return (
    <div className={`chat-message ${isUser ? 'chat-message-user' : 'chat-message-assistant'}`}>
      {/* ===== 头像 ===== */}
      <div className="chat-avatar">
        {isUser ? <UserIcon /> : <BotIcon />}
      </div>

      {/* ===== 消息体 ===== */}
      <div className="chat-bubble-wrap">
        {/* ---- AI 思考过程可折叠面板 ---- */}
        {!isUser && hasThinking && (
          <div className="chat-thinking">
            <button
              className="chat-thinking-toggle"
              onClick={onToggleThinking}
              aria-expanded={thinkingExpanded}
              aria-label={thinkingExpanded ? '收起思考过程' : '展开思考过程'}
            >
              <ChevronIcon open={thinkingExpanded} />
              <span className="chat-thinking-label">
                {isThinking ? (
                  // 思考中：紫色闪烁点
                  <span className="chat-thinking-active">
                    <span className="thinking-dot" />
                    思考中...
                  </span>
                ) : message.status === 'completed' ? (
                  '已深度思考'
                ) : (
                  '正在思考...'
                )}
              </span>
              <span className="chat-thinking-time">
                {formatRelativeTime(message.timestamp)}
              </span>
            </button>
            {/* 展开的思考内容 */}
            {thinkingExpanded && (
              <div className="chat-thinking-content">
                {message.thinking}
              </div>
            )}
          </div>
        )}

        {/* ---- 消息内容 ---- */}
        {message.content && (
          <div className="chat-bubble">
            {isUser ? (
              // 用户消息：纯文本
              <p className="chat-user-text">{message.content}</p>
            ) : (
              // AI 消息：Markdown 渲染 + 流式光标
              <>
                <ContentRenderer content={message.content} />
                {isStreaming && <span className="chat-cursor" />}
              </>
            )}
          </div>
        )}

        {/* ---- 思考中加载动画（无内容时） ---- */}
        {!isUser && !message.content && isThinking && (
          <div className="chat-bubble chat-bubble-loading">
            <span className="thinking-dot" />
            <span className="thinking-dot" />
            <span className="thinking-dot" />
          </div>
        )}

        {/* ---- 错误状态 ---- */}
        {message.status === 'error' && message.error && (
          <div className="chat-bubble chat-bubble-error">
            {message.error}
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageBubble;
